import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from './cn'

type Tone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger' | 'epic'
type Size = 'sm' | 'md'

const toneStyles: Record<Tone, string> = {
  neutral: 'bg-bg-muted text-fg-muted',
  accent:  'bg-accent-soft text-accent-hover',
  info:    'bg-info/15 text-info',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/25 text-fg',
  danger:  'bg-danger/15 text-danger',
  epic:    '', // uses style prop with epic color
}

const sizeStyles: Record<Size, string> = {
  sm: 'h-5 px-1.5 text-[10px] gap-1',
  md: 'h-6 px-2 text-xs gap-1.5',
}

type ChipProps = HTMLAttributes<HTMLSpanElement> & {
  tone?: Tone
  size?: Size
  /** For tone='epic', pass the hex color */
  color?: string
  dot?: boolean
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { tone = 'neutral', size = 'md', color, dot, className, style, children, ...rest },
  ref
) {
  const useColor = tone === 'epic' && color
  const finalStyle = useColor
    ? { backgroundColor: `${color}20`, color, ...style }
    : style

  return (
    <span
      ref={ref}
      style={finalStyle}
      className={cn(
        'inline-flex items-center rounded-full font-medium tracking-tight whitespace-nowrap',
        toneStyles[tone],
        sizeStyles[size],
        className,
      )}
      {...rest}
    >
      {dot && (
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={useColor ? { backgroundColor: color } : { backgroundColor: 'currentColor' }}
        />
      )}
      {children}
    </span>
  )
})

/** Map workflow_state.category → Chip tone */
export function statusTone(category: string): Tone {
  switch (category) {
    case 'todo':         return 'neutral'
    case 'in_progress':  return 'info'
    case 'in_review':    return 'warning'
    case 'done':         return 'success'
    default:             return 'neutral'
  }
}

export function priorityTone(priority: string): Tone {
  switch (priority) {
    case 'high': return 'danger'
    case 'med':  return 'warning'
    case 'low':  return 'neutral'
    default:     return 'neutral'
  }
}
